import { makeObservable, observable, computed, flow, action, runInAction } from "mobx";
import { isEqual } from "lodash";
import { IHospitalHoursModel, IStoreListingModel, HospitalModel, HospitalHoursModel, VeterinarianModel, HospitalScheduleModel, ClientConfigurationModel, IvlsDeviceModel } from "@common/models";
import { HospitalService } from "@common/services";
import { StoreSearchStore } from "@common/stores/search";
import { NotificationStoreInstance } from "@common/stores";
import { IQueryCacheStoreInit, QueryCacheStore } from "@common/stores/QueryCacheStore";

export interface IHospitalHoursGroup {
    hospitalHoursVisible: boolean,
    hospitalHoursConfirmationLoading: boolean,
    hospitalClosureChanged: boolean,
    hospitalHoursExceedWorkday: boolean,
    hospitalHoursConfirmed: boolean
}

export interface ILocationStoreInit extends IQueryCacheStoreInit {
    configuration: ClientConfigurationModel;
    tryUpdateCurrentHospital: (hospital?: HospitalModel) => void;
}

export class LocationStore extends QueryCacheStore {
    configuration: ClientConfigurationModel;
    searchStore: StoreSearchStore;
    tryUpdateCurrentHospital: (hospital?: HospitalModel) => void;
    @observable hospitalLoading: boolean = false;
    @observable hospital?: HospitalModel = undefined;
    @observable hospitalVeterinarians: VeterinarianModel[] = [];
    @observable hospitalSchedule: HospitalScheduleModel[] = [];
    @observable allHospitals: HospitalModel[] = [];
    @observable storeToConvert: IStoreListingModel | undefined = undefined;
    @observable storeIsBeingConverted: boolean = false;
    @observable hospitalHoursGroup: IHospitalHoursGroup = observable.object({
        hospitalHoursVisible: false,
        hospitalClosureChanged: false,
        hospitalHoursConfirmationLoading: false,
        hospitalHoursExceedWorkday: false,
        hospitalHoursConfirmed: false
    });
    @observable ivlsDevices: IvlsDeviceModel[] = [];

    constructor(init: ILocationStoreInit) {
        super(init)

        this.searchStore = new StoreSearchStore();
        this.tryUpdateCurrentHospital = init.tryUpdateCurrentHospital;
        this.configuration = init.configuration;

        makeObservable<LocationStore>(this, undefined, {
            autoBind: true
        }); //use decorators
    }

    @computed get isConfirmCreateHospitalModalVisible() {
        return this.storeToConvert !== undefined;
    }

    @computed get hospitalPricingTierOrganization() {
        return this.hospital && this.hospital.parentOrganizationId !== undefined ? this.configuration.getPricingTierOrganization(this.hospital.parentOrganizationId) : undefined;
    }

    @computed get confirmCreateHospitalModalDescription() {
        return this.storeToConvert
            ? `Are you sure you want to convert ${this.storeToConvert.name} in ${this.storeToConvert.city}, ${this.storeToConvert.stateAbbreviation} to a new hospital?`
            : '';
    }

    @computed get isLocationActionDisabled() {
        return this.searchStore.isLoading || this.hospitalLoading || this.storeToConvert !== undefined;
    }

    //#endregion

    //#region Actions
    /**
     * Clears all hospital hour changes needed for modal popu
     */
    @action.bound clearHospitalHoursConfirmation() {
        this.hospitalHoursGroup.hospitalClosureChanged = false;
        this.hospitalHoursGroup.hospitalHoursExceedWorkday = false;
        this.hospitalHoursGroup.hospitalHoursVisible = false;
        this.hospitalHoursGroup.hospitalHoursConfirmationLoading = false;
        this.hospitalHoursGroup.hospitalHoursConfirmed = false;
    }

    /**
     * Confirms that the user has agreed to make changes to hospital confirmation
     */
    @action.bound setHospitalHoursChangesConfirmed() {
        this.hospitalHoursGroup.hospitalHoursConfirmed = true;
    }

    @action.bound setStoreToConvert(store: IStoreListingModel) {
        this.storeIsBeingConverted = false;
        this.storeToConvert = store;
    }

    @action.bound clearStoreToConvert() {
        this.storeIsBeingConverted = false;
        this.storeToConvert = undefined;
    }
    //#endregion

    //#region Flows

    updateHospital = flow(function* (this: LocationStore, hospital: HospitalModel) {
        // if hospital hours were changed, we want to warn the user of this
        if (this.requireHospitalHoursConfirmation(hospital)) {
            this.hospitalHoursGroup.hospitalHoursVisible = true;
            return;
        }
        this.hospitalLoading = true;
        this.hospital = yield HospitalService.updateHospital(hospital.hospitalId, hospital)
            .then(NotificationStoreInstance.onSuccess("Hospital Updated!"))
            .catch(NotificationStoreInstance.onError(`Unable to update hospital: ${hospital.hospitalId}`, this.hospital));

        this.tryUpdateCurrentHospital(this.hospital);
        this.hospitalLoading = false;
        this.clearHospitalHoursConfirmation();
    });

    convertStoreToHospital = flow(function* (this: LocationStore) {
        if (this.storeToConvert === undefined) return;
        this.storeIsBeingConverted = true;
        this.hospital = yield HospitalService.convertStoreToHospital(this.storeToConvert.storeId)
            .then(NotificationStoreInstance.onSuccess("Hospital created!"))
            .catch(NotificationStoreInstance.onError(`Unable to convert store to hospital. Store: ${this.storeToConvert.storeId}`, undefined));

        this.clearStoreToConvert();
        return this.hospital === undefined ? 0 : this.hospital.hospitalId;
    });

    /**
     *  Retrieve the specified hospital by ID., the result is stored in the this.selectedLocation observable.
     * @param hospitalId the id of the hospital to load.
     * @returns
     */
    refreshHospital = flow(function* (this: LocationStore, hospitalId: number) {
        this.hospitalLoading = true;
        this.hospital = yield HospitalService.getHospital(hospitalId)
            .then((hh: HospitalModel) => {
                return runInAction(() => {
                    this.ivlsDevices = hh.ivlsDevices;
                    return hh;
                });
            })
            .catch(NotificationStoreInstance.onError("Unable to load Hospital", this.hospital))
        this.hospitalLoading = false;
    });

    /**
     * Determines if times specified by user are outside normal working hours
     * @param hospitalHours newly chosen hospital hours
     */
    hoursAreOutsideStandard = (hospitalHours: IHospitalHoursModel) => {
        // we don't need to check open and close times if the store is closed
        if (!hospitalHours.isOpen) {
            return false;
        }
        if (hospitalHours.openTime < "07:00:00" || hospitalHours.closeTime > "18:00:00") {
            return true;
        }
        return false;
    }

    requireHospitalHoursConfirmation = (hospital: HospitalModel) => {
        // if the user has accepted changes, then no more confirmation is needed
        if (this.hospitalHoursGroup.hospitalHoursConfirmed) {
            return false;
        }

        this.updateHospitalHoursConfirmationModel(hospital.hospitalHours);

        // we only want to show confirmation model if a store had been previously opened and now closed
        // and if a user specifies hours that are outside normal global work day
        if (this.hospitalHoursGroup.hospitalClosureChanged || this.hospitalHoursGroup.hospitalHoursExceedWorkday) {
            return true;
        }

        return false;
    }

    updateHospitalHoursConfirmationModel(allHospitalHours: HospitalHoursModel[]) {
        allHospitalHours.forEach((hospitalHour, index) => {
            const previousHospitalHours = this.hospital?.hospitalHours[index];
            // if previous and current are the same, no need to process anything
            if (isEqual(hospitalHour, previousHospitalHours)) {
                return;
            }
            // if a store has been closed, we need to confirm changes
            if (previousHospitalHours && previousHospitalHours.isOpen && !hospitalHour.isOpen) {
                this.hospitalHoursGroup.hospitalClosureChanged = true;
            }
            if (this.hoursAreOutsideStandard(hospitalHour)) {
                this.hospitalHoursGroup.hospitalHoursExceedWorkday = true;
            }
        })
    }

    loadAllHospitals = flow(function* (this: LocationStore) {
        this.allHospitals = yield HospitalService.getAllHospitals()
            .catch(NotificationStoreInstance.onError("Unable to load hospitals", []))
    });

    loadHospitalhospitalVeterinarians = flow(function* (this: LocationStore, hospitalId: number) {
        this.hospitalVeterinarians = yield this.queryClient?.fetchQuery({
            queryKey: [HospitalService.tags.getHospitalVeterinarians, { hospitalId }],
            queryFn: () => HospitalService.getHospitalVeterinarians(hospitalId),
            staleTime: this.queryStaleTime
        });
    });
    //#endregion

    public refreshHospitalIvlsDevices = flow(function* (this: LocationStore, hospitalId: number) {
        this.ivlsDevices = yield HospitalService.refreshHospitalIvlsDevices(hospitalId, { showSuccess: true, showError: true });
        if (this.hospital) {
            this.hospital.ivlsDevices = this.ivlsDevices;
        }
    });
}
